import { gql, useMutation } from '@apollo/client'
import { useCallback, useEffect, useRef } from 'react'
import { JiqooLogDisplayType } from '~/__generated__/graphql'

const ADD_JIQOO_LOG_MUTATION = gql`
  mutation AddJiqooLog($videoId: ID!, $displayType: JiqooLogDisplayType!) {
    addJiqooLog(input: { videoId: $videoId, displayType: $displayType }) {
      jiqooLog {
        id
      }
      errors
    }
  }
`

const UPDATE_JIQOO_LOG_MUTATION = gql`
  mutation UpdateJiqooLog($id: ID!, $viewingMin: Int!, $viewingSecond: Int!) {
    updateJiqooLog(input: { id: $id, viewingMin: $viewingMin, viewingSecond: $viewingSecond }) {
      jiqooLog {
        id
        viewingMin
        viewingSecond
        isViewed
      }
      errors
    }
  }
`

type UsePlaybackTrackingProps = {
  videoId: string
  displayType: JiqooLogDisplayType
  enabled?: boolean
}

type UsePlaybackTrackingReturn = {
  startTracking: () => void
  stopTracking: () => void
  pauseTracking: () => void
  resumeTracking: () => void
}

/**
 * 動画再生時間をトラッキングするカスタムフック
 * 
 * - 動画開始時にJiqooLogレコードを作成
 * - 10秒ごとに再生時間を更新
 * - 動画停止時に最終的な再生時間を送信
 */
export const usePlaybackTracking = ({
  videoId,
  displayType,
  enabled = true,
}: UsePlaybackTrackingProps): UsePlaybackTrackingReturn => {
  const [addJiqooLog] = useMutation(ADD_JIQOO_LOG_MUTATION)
  const [updateJiqooLog] = useMutation(UPDATE_JIQOO_LOG_MUTATION)

  const jiqooLogIdRef = useRef<string | null>(null)
  const startTimeRef = useRef<number | null>(null)
  const accumulatedTimeRef = useRef<number>(0) // 累積再生時間(ミリ秒)
  const lastUpdateTimeRef = useRef<number>(0) // 前回更新した累積時間(ミリ秒)
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null)
  const isPausedRef = useRef<boolean>(false)
  const currentVideoIdRef = useRef<string>(videoId)
  const lastUpdateWasFromPauseRef = useRef<boolean>(false) // pause由来の更新かどうか

  // videoIdが変更された場合、前の動画のトラッキングを停止
  useEffect(() => {
    if (currentVideoIdRef.current !== videoId) {
      stopTracking()
      currentVideoIdRef.current = videoId
    }
  }, [videoId])

  // 再生時間を更新する関数
  const updatePlaybackTime = useCallback(async (force: boolean = false) => {
    if (!jiqooLogIdRef.current) return

    let totalMilliseconds: number

    if (isPausedRef.current) {
      // 一時停止中の場合は累積時間のみを使用
      totalMilliseconds = accumulatedTimeRef.current
    } else if (startTimeRef.current) {
      // 再生中の場合は累積時間 + 経過時間（ミリ秒単位）
      const currentTime = Date.now()
      const elapsedMilliseconds = currentTime - startTimeRef.current
      totalMilliseconds = accumulatedTimeRef.current + elapsedMilliseconds
    } else {
      return
    }

    // 前回の更新から10秒以上経過しているかチェック（強制更新でない場合）
    if (!force && totalMilliseconds - lastUpdateTimeRef.current < 10000) {
      return
    }

    // ミリ秒を秒に変換（小数点以下切り捨て）
    const totalSeconds = Math.floor(totalMilliseconds / 1000)
    const viewingMin = Math.floor(totalSeconds / 60)
    const viewingSecond = totalSeconds % 60

    try {
      await updateJiqooLog({
        variables: {
          id: jiqooLogIdRef.current,
          viewingMin,
          viewingSecond,
        },
      })
      // 更新成功時に最終更新時刻を記録（ミリ秒）
      lastUpdateTimeRef.current = totalMilliseconds
    } catch (error) {
      console.error('[usePlaybackTracking] Failed to update JiqooLog:', error)
    }
  }, [updateJiqooLog])

  // トラッキング開始
  const startTracking = useCallback(async () => {
    if (!enabled) return

    // 既存のログIDがある場合は、再開のみ（新規作成しない）
    if (jiqooLogIdRef.current && currentVideoIdRef.current === videoId) {
      // 既にインターバルが動いている場合はスキップ
      if (intervalIdRef.current) {
        return
      }
      
      // 再開：startTimeをリセット（累積時間は保持 - 同一ID内で加算し続ける）
      startTimeRef.current = Date.now()
      isPausedRef.current = false
      lastUpdateWasFromPauseRef.current = false
      // accumulatedTimeRef と lastUpdateTimeRef は保持（累積時間を継続）
      
      // インターバル再開
      intervalIdRef.current = setInterval(() => {
        updatePlaybackTime()
      }, 1000)
      
      return
    }

    // 新規作成（初回再生 or 動画変更時）
    try {
      // JiqooLogレコードを作成
      const { data } = await addJiqooLog({
        variables: {
          videoId,
          displayType,
        },
      })

      const jiqooLogId = data?.addJiqooLog?.jiqooLog?.id
      if (jiqooLogId) {
        jiqooLogIdRef.current = jiqooLogId
        startTimeRef.current = Date.now()
        accumulatedTimeRef.current = 0
        lastUpdateTimeRef.current = 0
        isPausedRef.current = false
        currentVideoIdRef.current = videoId

        // 1秒ごとにチェックして、10秒経過していれば更新
        intervalIdRef.current = setInterval(() => {
          updatePlaybackTime()
        }, 1000)
      }
    } catch (error) {
      console.error('Failed to create JiqooLog:', error)
    }
  }, [enabled, videoId, displayType, addJiqooLog, updatePlaybackTime])

  // トラッキング停止（動画終了・ページ遷移など）
  const stopTracking = useCallback(async () => {
    // 既に停止済みの場合は何もしない（重複実行防止）
    if (!jiqooLogIdRef.current) {
      return
    }

    // インターバルが動いていない場合は、既に処理済みなので何もしない
    if (!intervalIdRef.current) {
      return
    }

    // インターバルをクリア
    clearInterval(intervalIdRef.current)
    intervalIdRef.current = null

    // 最終的な再生時間を計算
    let totalMilliseconds: number

    if (isPausedRef.current) {
      // 一時停止中の場合は累積時間のみ
      totalMilliseconds = accumulatedTimeRef.current
    } else if (startTimeRef.current) {
      // 再生中の場合は累積時間 + 経過時間（ミリ秒単位）
      const currentTime = Date.now()
      const elapsedMilliseconds = currentTime - startTimeRef.current
      totalMilliseconds = accumulatedTimeRef.current + elapsedMilliseconds
    } else {
      totalMilliseconds = accumulatedTimeRef.current
    }

    // 直前にpauseTrackingから更新した場合、同じ時間なのでスキップ
    if (lastUpdateWasFromPauseRef.current && totalMilliseconds === lastUpdateTimeRef.current) {
      // 状態をリセット（累積時間は保持 - 同一ID内で加算し続ける）
      startTimeRef.current = null
      isPausedRef.current = false
      lastUpdateWasFromPauseRef.current = false
      // accumulatedTimeRef, lastUpdateTimeRef, jiqooLogIdRef は保持（累積時間を継続）
      return
    }

    // ミリ秒を秒に変換（小数点以下切り捨て）
    const totalSeconds = Math.floor(totalMilliseconds / 1000)
    const viewingMin = Math.floor(totalSeconds / 60)
    const viewingSecond = totalSeconds % 60

    // jiqooLogIdを保存
    const logId = jiqooLogIdRef.current
    
    // リセット（jiqooLogIdと累積時間は保持 - 同一ID内で加算し続ける）
    startTimeRef.current = null
    isPausedRef.current = false
    lastUpdateWasFromPauseRef.current = false
    // accumulatedTimeRef, lastUpdateTimeRef, jiqooLogIdRef は保持（累積時間を継続）

    // 最終的な再生時間を送信
    try {
      await updateJiqooLog({
        variables: {
          id: logId,
          viewingMin,
          viewingSecond,
        },
      })
    } catch (error) {
      console.error('[usePlaybackTracking] Failed to update JiqooLog on stop:', error)
    }
  }, [updateJiqooLog])

  // トラッキング一時停止
  const pauseTracking = useCallback(async () => {
    if (!startTimeRef.current || isPausedRef.current) {
      return
    }

    // 現在までの再生時間を累積(ミリ秒単位)
    const currentTime = Date.now()
    const elapsedMilliseconds = currentTime - startTimeRef.current
    accumulatedTimeRef.current += elapsedMilliseconds

    isPausedRef.current = true
    startTimeRef.current = null

    // pause経由の更新であることをマーク（await前に設定）
    lastUpdateWasFromPauseRef.current = true
    // lastUpdateTimeRefも先に更新（stopTrackingの重複チェックのため）
    lastUpdateTimeRef.current = accumulatedTimeRef.current

    // 一時停止時に現在の再生時間を送信（強制更新）
    await updatePlaybackTime(true)
  }, [updatePlaybackTime])

  // トラッキング再開
  const resumeTracking = useCallback(() => {
    if (!isPausedRef.current) return

    startTimeRef.current = Date.now()
    isPausedRef.current = false
  }, [])

  // コンポーネントアンマウント時にトラッキング停止
  useEffect(() => {
    return () => {
      stopTracking()
    }
  }, [stopTracking])

  // ページ遷移時にトラッキング停止
  // 注意: バックグラウンドでもカウントを続ける仕様のため、visibilitychangeでは停止しない
  useEffect(() => {
    const handleBeforeUnload = () => {
      stopTracking()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [stopTracking])

  return {
    startTracking,
    stopTracking,
    pauseTracking,
    resumeTracking,
  }
}
